import React, { useMemo } from 'react';

/**
 * Parses a comment body for @username tokens and renders matches as styled
 * inline chips. Plain-text fragments render as-is. Used in CommentThread (and
 * later in other comment surfaces) so posted mentions visually stand out
 * from surrounding text — same visual cue as Slack/Notion/Linear.
 *
 * Why no user-data lookup:
 *   - The component is intentionally cheap. It doesn't fetch user data to
 *     resolve the username to a full name; it just renders `@username` as
 *     a chip. The chip itself is the visual cue ("hey, this is a mention").
 *     Resolving display names would require either (a) prefetching a user
 *     list everywhere comments render or (b) extending the comments API
 *     response to embed resolved mentions — both are heavier than v1 needs.
 *   - The mention notification itself already tells the recipient WHO
 *     mentioned them by name. The chip just visually distinguishes the
 *     mention from regular text in the rendered comment thread.
 *
 * Regex matches `@` followed by `[a-zA-Z0-9_.-]+` — exact mirror of
 * ByMentionResolver.php's pattern + CommentMentionService::MENTION_PATTERN.
 * Keeping these three in sync is essential: anything the backend recognises
 * as a mention should also visually highlight on the frontend.
 *
 * Edge cases handled:
 *   - Empty / null text → renders empty fragment
 *   - No @ tokens → renders the text unchanged (no extra spans)
 *   - Multiple @ tokens in one body → each chips independently
 *   - `@` preceded by a non-whitespace char (like email `foo@bar`) →
 *     NOT chipped, matching the backend's "must be start-of-string or
 *     after whitespace" rule. Mirror of ByMentionResolver's behaviour
 *     so the visual treatment never claims a token is a mention when
 *     the backend would have ignored it.
 */
interface Props {
    /** The raw comment body as stored in the DB. May contain @mentions. */
    text: string;
}

// Same pattern used backend-side. Kept as const so we can reuse + share with
// a hypothetical helper test later.
const MENTION_PATTERN = /(^|\s)@([a-zA-Z0-9_.-]+)/g;

const MentionText: React.FC<Props> = ({ text }) => {
    const nodes = useMemo(() => {
        if (!text) return null;

        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        // Reset lastIndex (defensive — regex is module-scoped, calling exec
        // mutates state).
        MENTION_PATTERN.lastIndex = 0;

        while ((match = MENTION_PATTERN.exec(text)) !== null) {
            const [whole, leading, username] = match;
            // match.index points at the start of the captured group, which
            // includes the leading whitespace (or '' if start-of-string).
            // The actual @ position is match.index + leading.length.
            const atPos = match.index + leading.length;

            // Plain text BEFORE this mention (everything from lastIndex to
            // the start of the @ token, INCLUDING the leading whitespace
            // we matched as part of the lookbehind workaround).
            if (atPos > lastIndex) {
                parts.push(text.substring(lastIndex, atPos));
            }

            // The mention chip itself.
            parts.push(
                <span
                    key={`mention-${atPos}-${username}`}
                    className="mention-chip"
                    // No href — the chip is informational, not actionable in
                    // v1. A future enhancement could route to the user's
                    // profile page if/when we have one.
                    title={`@${username}`}
                >
                    @{username}
                </span>
            );

            lastIndex = atPos + 1 + username.length; // +1 for the @ itself
        }

        // Trailing plain text after the last mention.
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        // Wrap each piece in a key-safe Fragment when multiple. If no
        // mentions matched, parts is empty — return the raw text as a single
        // string so React renders it directly without extra wrappers.
        if (parts.length === 0) return text;
        return parts;
    }, [text]);

    // Render. Falsy guard so MentionText with no text doesn't introduce
    // dangling whitespace.
    if (!nodes) return null;
    return <>{nodes}</>;
};

export default MentionText;
